/**
 * Suchausdrücke der Regeln als Text für den Editor (isomorph, SPA und API).
 *
 * Eine Zeile ist eine Alternative (ODER), die Begriffe einer Zeile gelten zusammen (UND):
 * `wort`, `-wort` (darf nicht vorkommen), `"genaue Wortfolge"` oder „…“, `nr:310000000001,…`
 * (bestimmte Angebote). `*` ist keine Wildcard, sondern ein gewöhnliches Zeichen. „Alle Angebote“
 * wählt die UI ausdrücklich, nie über eine leere Seite. Nicht übersetzbare Altbegriffe
 * (`unsupported`) lassen sich nicht als Text darstellen; solche Regeln bleiben unverändert.
 *
 * `termIssue` bildet die Prüfung der Regel-Engine nach (`packages/rules/src/search.ts`): Ein Wort
 * muss nach der Normalisierung genau ein Token aus Buchstaben und Ziffern sein, sonst lehnt die
 * Engine die ganze Galerie ab. Die API prüft zusätzlich mit `validateRuleSetDraft` der Engine.
 */
import type { SearchExpr } from '@pat/core/rules';

type Term = SearchExpr['alternatives'][number]['terms'][number];

export const ALL_EXPR: SearchExpr = { alternatives: [{ terms: [{ kind: 'all' }] }] };

export function isAllExpr(expr: SearchExpr | undefined): boolean {
  return expr !== undefined && expr.alternatives.length === 1 && expr.alternatives[0]!.terms.length === 1 && expr.alternatives[0]!.terms[0]!.kind === 'all';
}

export function hasUnsupported(expr: SearchExpr | undefined): boolean {
  return expr?.alternatives.some(alternative => alternative.terms.some(term => term.kind === 'unsupported')) ?? false;
}

export type ParsedSearch = { ok: true; expr: SearchExpr } | { ok: false; error: string };

const ITEM_ID = /^[1-9][0-9]{0,19}$/;
const FORBIDDEN = /[?()[\]{};"<=>|]/u;

/** Wie `normalizeTitle` in core: NFKC, klein, Leerraum zusammengefasst. */
function normalize(value: string): string {
  return value.normalize('NFKC').toLowerCase().replace(/\s+/g, ' ').trim();
}

/** Meldung, wenn die Engine diesen Begriff ablehnen würde; sonst `undefined`. */
export function termIssue(term: Term): string | undefined {
  if (term.kind !== 'word' && term.kind !== 'not-word' && term.kind !== 'phrase') return undefined;
  if (FORBIDDEN.test(term.value)) return `„${term.value.slice(0, 40)}“ enthält ein nicht erlaubtes Zeichen (? ( ) [ ] { } ; " < = > |)`;
  const normalized = normalize(term.value);
  if (normalized.includes('*')) {
    if (term.kind !== 'phrase' && !/^[\p{L}\p{N}*]+$/u.test(normalized)) return `„${term.value.slice(0, 40)}“: mit * nur Buchstaben und Ziffern`;
    return undefined;
  }
  const tokens = normalized.match(/[\p{L}\p{N}]+/gu) ?? [];
  if (tokens.length === 0) return `„${term.value.slice(0, 40)}“ enthält keine Buchstaben oder Ziffern`;
  if (term.kind !== 'phrase' && (tokens.length !== 1 || tokens[0] !== normalized)) {
    return term.kind === 'not-word'
      ? `„-${term.value.slice(0, 40)}“: Ausschluss nur für ein einzelnes Wort aus Buchstaben und Ziffern`
      : `„${term.value.slice(0, 40)}“ ist kein einzelnes Wort; als "Wortfolge" in Anführungszeichen eingeben`;
  }
  return undefined;
}

export function exprIssue(expr: SearchExpr | undefined): string | undefined {
  for (const alternative of expr?.alternatives ?? []) {
    for (const term of alternative.terms) {
      const issue = termIssue(term);
      if (issue) return issue;
    }
  }
  return undefined;
}

function tokens(line: string): string[] | string {
  const result: string[] = [];
  let rest = line.trim();
  while (rest.length > 0) {
    const quote = rest[0] === '"' ? '"' : rest[0] === '„' ? '“' : undefined;
    if (quote) {
      const end = rest.indexOf(quote, 1);
      if (end < 0) return 'Anführungszeichen nicht geschlossen';
      result.push(rest.slice(0, end + 1));
      rest = rest.slice(end + 1).trimStart();
      continue;
    }
    const space = rest.search(/\s/);
    result.push(space < 0 ? rest : rest.slice(0, space));
    rest = space < 0 ? '' : rest.slice(space).trimStart();
  }
  return result;
}

function term(token: string): Term | string {
  if (token.startsWith('"') || token.startsWith('„')) {
    const value = token.slice(1, -1).trim().replace(/\s+/g, ' ');
    if (!value) return 'Leere Wortfolge';
    if (value.length > 200) return 'Wortfolge zu lang (höchstens 200 Zeichen)';
    return { kind: 'phrase', value };
  }
  if (token.toLowerCase().startsWith('nr:')) {
    const ids = token.slice(3).split(',').filter(Boolean);
    if (ids.length === 0 || ids.some(id => !ITEM_ID.test(id))) return `Ungültige Angebotsnummer in „${token.slice(0, 40)}“`;
    if (ids.length > 500) return 'Höchstens 500 Angebotsnummern je Begriff';
    return { kind: 'item-ids', ids: [...new Set(ids)] };
  }
  if (token.startsWith('-"') || token.startsWith('-„')) return 'Ausschluss nur für einzelne Wörter, nicht für Wortfolgen';
  if (token.startsWith('-') && token.length > 1) {
    const value = token.slice(1);
    if (value.length > 100) return 'Begriff zu lang (höchstens 100 Zeichen)';
    return { kind: 'not-word', value };
  }
  if (token.length > 100) return 'Begriff zu lang (höchstens 100 Zeichen)';
  return { kind: 'word', value: token };
}

/** Text → Suchausdruck; Fehler mit verständlicher Meldung statt eines stillen Verwerfens. */
export function parseSearchText(text: string): ParsedSearch {
  const lines = text.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
  if (lines.length === 0) return { ok: false, error: 'Bitte mindestens einen Begriff eingeben oder „Alle Angebote“ wählen' };
  if (lines.length > 500) return { ok: false, error: 'Höchstens 500 Zeilen' };
  const alternatives: SearchExpr['alternatives'] = [];
  for (const [index, line] of lines.entries()) {
    const parts = tokens(line);
    if (typeof parts === 'string') return { ok: false, error: `Zeile ${index + 1}: ${parts}` };
    if (parts.length > 50) return { ok: false, error: `Zeile ${index + 1}: höchstens 50 Begriffe` };
    const terms: Term[] = [];
    for (const part of parts) {
      const parsed = term(part);
      if (typeof parsed === 'string') return { ok: false, error: `Zeile ${index + 1}: ${parsed}` };
      const issue = termIssue(parsed);
      if (issue) return { ok: false, error: `Zeile ${index + 1}: ${issue}` };
      terms.push(parsed);
    }
    if (terms.every(t => t.kind === 'not-word')) return { ok: false, error: `Zeile ${index + 1}: nur Ausschlüsse, bitte mindestens einen gesuchten Begriff angeben` };
    alternatives.push({ terms });
  }
  return { ok: true, expr: { alternatives } };
}

/** Suchausdruck → Text; `undefined`, wenn er nicht als Text darstellbar ist (alle, unsupported). */
export function formatSearchText(expr: SearchExpr | undefined): string | undefined {
  if (!expr || isAllExpr(expr) || hasUnsupported(expr)) return undefined;
  const lines: string[] = [];
  for (const alternative of expr.alternatives) {
    const parts: string[] = [];
    for (const t of alternative.terms) {
      if (t.kind === 'word') parts.push(t.value);
      else if (t.kind === 'not-word') parts.push(`-${t.value}`);
      else if (t.kind === 'phrase') parts.push(`"${t.value}"`);
      else if (t.kind === 'item-ids') parts.push(`nr:${t.ids.join(',')}`);
      else return undefined;
    }
    lines.push(parts.join(' '));
  }
  return lines.join('\n');
}
